import { defineComponent as _defineComponent } from 'vue'
import {useTranslations} from "@/hooks/useTranslations";
import Package from "@/models/Subscriptions/SubscriptionPackages/Package";
import {useStoreState} from "@/hooks/useStoreState";
import {SubscriptionStatus} from "@/models/Subscriptions/SubscriptionStatus";
import VPaymentMethods from "@/components/vPaymentMethods.vue";
import {dateFormatter} from "@/utils/Helpers";
import Vue, {computed, onMounted, Ref, ref} from "vue";
import CouponRedemptionForm from "@/components/forms/SubscribeToPackageForm/CouponRedemptionForm.vue";
import Coupon from "@/models/Coupon/Coupon";
import UserLicensesForm from "@/components/forms/SubscribeToPackageForm/UserLicensesForm.vue";
import AddOnForm from "@/components/forms/SubscribeToPackageForm/AddOnForm.vue";
import PackageDetails from "@/components/forms/SubscribeToPackageForm/PackageDetails.vue";
import CostSummary from "@/components/forms/SubscribeToPackageForm/CostSummary.vue";
import NotificationEmailForm from "@/components/forms/SubscribeToPackageForm/NotificationEmailForm.vue";
import CheckSubscriptionMigration from "@/models/Subscriptions/SubscriptionPackages/CheckSubscriptionMigration";
import SubscriptionRepository from "@/utils/API/SubscriptionRepository";
import {SubscriptionMigration} from "@/models/Subscriptions/SubscriptionPackages/SubscriptionMigration";
import {PaymentProvider} from "@/models/Subscriptions/PaymentProvider";
import {SubscriptionMigrationTypeEnum} from "@/models/Subscriptions/SubscriptionPackages/SubscriptionMigrationTypeEnum";
import VPreLoader from "@/components/vPreLoader.vue";
import {useNotify} from "@/hooks/useNotify";
import VBanner from "@/components/vBanner.vue";
import {AddOnType} from "@/models/Subscriptions/AddOns/AddOnType";


type SubscribeToPackageFormProps = {
  packageId: number,
  isUpgrade: boolean,
  isDowngrade: boolean
};


export default /*#__PURE__*/_defineComponent({
  __name: 'SubscribeToPackageForm',
  props: {
    packageId: null,
    isUpgrade: { type: Boolean },
    isDowngrade: { type: Boolean }
  },
  emits: ["payment-success", "payment-failed"],
  setup(__props: any, { emit }) {

const props = __props as {
  packageId: number,
  isUpgrade: boolean,
  isDowngrade: boolean
};

const translations = useTranslations();
const storeState = useStoreState();
const notify = useNotify();


const coupon = ref<Coupon>();
const subscriptionMigration: Ref<SubscriptionMigration | null> = ref(null);
const isLoading = ref(false);
const packageLoading = ref(true);
const paidUserLicenses = ref(0);
const notificationEmail = ref(storeState.value.AccountModule.userDetails.restaurantNotificationEmail);
const selectedPackage: Ref<Package | null> = ref(null);
const isAddingNewPaymentMethod = ref(false);

const selectedPaymentMethodId: Ref<number | null> = ref(null);
const editingPaymentMethodId = ref(-1);



onMounted(async() => {
  await getPackage(props.packageId);
  await checkMigrationType();
});

const isTrialOrExpired = computed(() => (
  storeState.value.AccountModule.userDetails.isTrial
  || storeState.value.AccountModule.userDetails.subscriptionStatus == SubscriptionStatus.Expired
));
const isFutureSubscription = computed(() => {
  return (
    (storeState.value.AccountModule.userDetails.isTrial
      && storeState.value.AccountModule.userDetails.featureFlagPaymentProvider === PaymentProvider.Braintree)
    || (
      storeState.value.AccountModule.userDetails.featureFlagPaymentProvider === PaymentProvider.Braintree
      && storeState.value.AccountModule.userDetails.subscriptionPaymentProvider == PaymentProvider.Braintree
    )
  );
});

const isProratedUpgrade = computed(() => {
  if (storeState.value.AccountModule.userDetails.subscriptionPaymentProvider == PaymentProvider.FastSpring) {
    return props.isUpgrade;
  }

  else {
    return false;
  }
});

const subscribeButtonText = computed(() => {
  if (subscriptionMigration.value != null) {
    return subscriptionMigration.value.subscriptionMigrationType === SubscriptionMigrationTypeEnum.Upgrade
      ? translations.value.get("ms_upgrade")
      : translations.value.get("ms_downgrade");
  }

  return "";
});

const shouldDisablePayButton = computed(() => {
  return !selectedPaymentMethodId.value || isAddingNewPaymentMethod.value;
});

function handleAddingNewPaymentMethod(state: boolean) {
  isAddingNewPaymentMethod.value = state;
}

async function checkMigrationType() {
  if (selectedPackage.value) {
    //If the subscription is not trial or expired, check for subscription migration
    if (
      !storeState.value.AccountModule.userDetails.isTrial &&
      storeState.value.AccountModule.userDetails.subscriptionStatus !== SubscriptionStatus.Expired
    ) {
      isLoading.value = true;

      let checkSubscriptionMigrationDto: CheckSubscriptionMigration = {
        subscriptionPackageId: selectedPackage.value.id,
        addOns: selectedPackage.value.addOns
          .filter((x) => x.checked)
          .map((addon) => {
            return addon.addOn;
          }),
        paidUserLicenses: paidUserLicenses.value,
      };

      try {
        const response = await SubscriptionRepository.checkSubscriptionMigration(checkSubscriptionMigrationDto);
        subscriptionMigration.value = response.data.result;
      }

      catch (error) {
        console.error(error);
      }

      finally {
        isLoading.value = false;
      }
    }
  }
}

function onAddOnSelectionChange(index: number, checked: boolean) {
  selectedPackage.value.addOns[index].checked = checked;
  Vue.set(selectedPackage.value.addOns, index, selectedPackage.value.addOns[index]);
  checkMigrationType();
}

function paymentMethodChanged(paymentMethodId: number) {
  console.log("Payment method changed: ", paymentMethodId);
  selectedPaymentMethodId.value = paymentMethodId;
}

function editCreditCard(cardId: number) {
  if (editingPaymentMethodId.value === cardId) {
    editingPaymentMethodId.value = -1;
  }
  else {
    editingPaymentMethodId.value = cardId;
  }
}

async function getPackage(packageId: number) {
  packageLoading.value = true;
  try {
    const packageResponse = await SubscriptionRepository.getPackageById(packageId);
    selectedPackage.value = packageResponse.data;
    selectedPackage.value.addOns = selectedPackage.value.addOns
      .filter((addOn) => addOn.addOn !== AddOnType.DownloadLabels);
  }

  catch (error) {
    console.error(error);
  }

  finally {
    packageLoading.value = false;
  }
}

async function pay() {
  await payForSubscription(null);
}

async function payForSubscription(orderId: number) {
  isLoading.value = true;

  let chosenAddons = selectedPackage.value
    ? selectedPackage.value.addOns
      .filter((x) => x.checked)
      .map((addon) => {
        return addon.addOn;
      })
    : [];

  const subscribeDto = {
    orderId: orderId,
    subscriptionPackageId: selectedPackage.value.id,
    paymentMethodId: selectedPaymentMethodId.value,
    notificationEmail: notificationEmail.value,
    addOns: chosenAddons,
    paidUserLicenses: paidUserLicenses.value,
    couponId: 0,
  };

  let subscriptionFunction;

  // If the subscription is trial or expired, subscribe
  if (isTrialOrExpired.value) {
    if (coupon.value) {
      subscribeDto.couponId = coupon.value.id;
    }

    subscriptionFunction = SubscriptionRepository.subscribe;
  }

  else if (props.isUpgrade) {
    subscriptionFunction = SubscriptionRepository.upgradeSubscription;
  }

  else {
    subscriptionFunction = SubscriptionRepository.downgradeSubscription;
  }

  try {
    await subscriptionFunction(subscribeDto);
    notify.success("Processing payment..");
    emit("payment-success", chosenAddons);
  }

  catch (error) {
    let message;
    if (
      error.response &&
      error.response.data != null &&
      error.response.data.errorMessages != null &&
      error.response.data.errorMessages.length > 0
    ) {
      message = error.response.data.errorMessages[0].userVisibleMessage;
    }

    else {
      message = translations.value.get("network_error");
    }

    notify.error(message);
    emit('payment-failed');
  }

  finally {
    isLoading.value = false;
  }
}

return { __sfc: true,translations, storeState, notify, props, coupon, subscriptionMigration, isLoading, packageLoading, paidUserLicenses, notificationEmail, selectedPackage, isAddingNewPaymentMethod, selectedPaymentMethodId, editingPaymentMethodId, emit, isTrialOrExpired, isFutureSubscription, isProratedUpgrade, subscribeButtonText, shouldDisablePayButton, handleAddingNewPaymentMethod, checkMigrationType, onAddOnSelectionChange, paymentMethodChanged, editCreditCard, getPackage, pay, payForSubscription, VPaymentMethods, dateFormatter, CouponRedemptionForm, UserLicensesForm, AddOnForm, PackageDetails, CostSummary, NotificationEmailForm, VPreLoader, VBanner }
}

})